export interface AnalyseRow {
  properties?: Record<string, unknown> | null;
}

export const DEFAULT_SCATTER_POINT_OPACITY = 0.88;

export const SCATTER_CATEGORY_COLOURS = [
  '#0369a1', '#7c3aed', '#c2410c', '#b91c1c',
  '#0f766e', '#a16207', '#be185d', '#334155',
];

export function scatterPointOpacity(
  configuredOpacity: number,
  hasSelection: boolean,
  isSelected: boolean,
): number {
  const opacity = Math.min(1, Math.max(0.15, configuredOpacity));
  if (!hasSelection) return opacity;
  return isSelected ? 1 : opacity;
}

export function scatterPointSize(baseSize: number, isSelected: boolean): number {
  return baseSize + (isSelected ? 5 : 0);
}

export function plotlySelectedPoints(indices: number[]): number[] | undefined {
  return indices.length > 0 ? indices : undefined;
}

/**
 * Keep the source order within each group while bringing a brushed selection
 * together for inspection in the linked compound table.
 */
export function partitionRowsBySelection<T>(
  rows: T[],
  selectedIds: Set<number>,
  getId: (row: T) => number,
): { selected: T[]; unselected: T[] } {
  if (selectedIds.size === 0) return { selected: [], unselected: rows };
  const selected: T[] = [];
  const unselected: T[] = [];
  rows.forEach(row => {
    (selectedIds.has(getId(row)) ? selected : unselected).push(row);
  });
  return { selected, unselected };
}

export interface PlotlySelectionPoint {
  customdata?: unknown;
  curveNumber?: number;
  pointNumber?: number;
  pointIndex?: number;
}

export interface PlotlySelectionEvent {
  points?: PlotlySelectionPoint[] | null;
}

function finiteRowId(value: unknown): number | null {
  const candidate = Array.isArray(value) ? value[0] : value;
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
  if (typeof candidate === 'string' && candidate.trim() !== '') {
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Resolve Plotly selection points to stable molecule IDs.
 *
 * pointNumber/pointIndex are local to each trace, so they cannot be treated as
 * global row indexes when categorical colouring is rendered as multiple
 * traces. customdata is preferred; trace customdata is the safe fallback.
 */
export function selectedRowIdsFromPlotlyEvent(
  event: PlotlySelectionEvent | null | undefined,
  traces: Array<{ customdata?: unknown[] }> = [],
): Set<number> {
  const ids = new Set<number>();
  for (const point of event?.points || []) {
    let id = finiteRowId(point.customdata);
    if (id === null && Number.isInteger(point.curveNumber)) {
      const localIndex = Number.isInteger(point.pointNumber)
        ? point.pointNumber
        : point.pointIndex;
      if (Number.isInteger(localIndex)) {
        id = finiteRowId(traces[point.curveNumber as number]?.customdata?.[localIndex as number]);
      }
    }
    if (id !== null) ids.add(id);
  }
  return ids;
}

const NON_DATA_KEYS = new Set([
  'id', 'file_id', 'smiles', 'molfile', 'inchi', 'structure', 'svg', 'image',
]);

const NUMERIC_RESULT = /^\s*(?:<=|>=|<|>|~|≈)?\s*[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/;

export function isImportedNumeric(value: unknown): boolean {
  if (typeof value === 'number') return Number.isFinite(value);
  return typeof value === 'string' && NUMERIC_RESULT.test(value.trim());
}

export function discoverImportedColumns(rows: AnalyseRow[], declaredColumns: string[] = []) {
  const all = new Set(declaredColumns.filter(column => !NON_DATA_KEYS.has(column)));
  rows.forEach(row => Object.keys(row.properties || {}).forEach(column => {
    if (!NON_DATA_KEYS.has(column)) all.add(column);
  }));

  const allColumns = Array.from(all);
  const numeric = allColumns.filter(column => rows.some(row => isImportedNumeric(row.properties?.[column])));
  const categorical = allColumns.filter(column => {
    if (numeric.includes(column)) return false;
    return rows.some(row => {
      const value = row.properties?.[column];
      return value !== null && value !== undefined && String(value).trim() !== '';
    });
  });
  return { all: allColumns, numeric, categorical };
}

export function buildMarginalHistogramTraces(
  xs: number[],
  ys: number[],
  xLabel: string,
  yLabel: string,
  bins: number,
) {
  return [
    {
      x: xs,
      type: 'histogram',
      xaxis: 'x',
      yaxis: 'y2',
      nbinsx: bins,
      name: `${xLabel} distribution`,
      marker: { color: 'rgba(15,159,143,0.52)' },
      hovertemplate: `${xLabel}: %{x}<br>Count: %{y}<extra></extra>`,
      showlegend: false,
    },
    {
      y: ys,
      type: 'histogram',
      orientation: 'h',
      xaxis: 'x2',
      yaxis: 'y',
      nbinsy: bins,
      name: `${yLabel} distribution`,
      marker: { color: 'rgba(15,159,143,0.52)' },
      hovertemplate: `${yLabel}: %{y}<br>Count: %{x}<extra></extra>`,
      showlegend: false,
    },
  ];
}
